import { FiRefreshCw } from 'react-icons/fi';

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  unit?: string;
}

function Slider({ label, value, min, max, step = 1, onChange, unit = '' }: SliderProps) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className="text-sm text-gray-500">{value}{unit}</span>
      </div>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-500"
      />
    </div>
  );
}

interface LineArtSettingsProps {
  settings: {
    algorithm: 'ai' | 'canny';
    edgeStrength: number;
    cannyThresholdLow: number;
    cannyThresholdHigh: number;
    blurKernelSize: number;
    lineThickness: number;
    invertColors: boolean;
  };
  onChange: (settings: LineArtSettingsProps['settings']) => void;
  onPreview: () => void;
  isLoading?: boolean;
}

export function LineArtSettings({ settings, onChange, onPreview, isLoading }: LineArtSettingsProps) {
  const isAI = settings.algorithm === 'ai';

  return (
    <div className="space-y-6">
      <h3 className="font-semibold text-gray-900">Line Art Settings</h3>

      {/* Algorithm Toggle */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Detection Method</label>
        <div className="flex rounded-lg overflow-hidden border border-gray-200">
          <button
            onClick={() => onChange({ ...settings, algorithm: 'ai' })}
            className={`flex-1 py-2 px-3 text-sm font-medium transition-colors ${
              isAI
                ? 'bg-indigo-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            AI (Better Quality)
          </button>
          <button
            onClick={() => onChange({ ...settings, algorithm: 'canny' })}
            className={`flex-1 py-2 px-3 text-sm font-medium transition-colors ${
              !isAI
                ? 'bg-indigo-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Classic (Faster)
          </button>
        </div>
        <p className="text-xs text-gray-500">
          {isAI
            ? 'Neural network edge detection - slower but higher quality'
            : 'Traditional Canny algorithm - fast with manual tuning'}
        </p>
      </div>

      {/* AI-specific settings */}
      {isAI && (
        <Slider
          label="Edge Sensitivity"
          value={settings.edgeStrength}
          min={0.1}
          max={0.9}
          step={0.05}
          onChange={(v) => onChange({ ...settings, edgeStrength: v })}
        />
      )}

      {/* Canny-specific settings */}
      {!isAI && (
        <>
          <Slider
            label="Edge Detection Low"
            value={settings.cannyThresholdLow}
            min={0}
            max={200}
            onChange={(v) => onChange({ ...settings, cannyThresholdLow: v })}
          />

          <Slider
            label="Edge Detection High"
            value={settings.cannyThresholdHigh}
            min={50}
            max={300}
            onChange={(v) => onChange({ ...settings, cannyThresholdHigh: v })}
          />

          <Slider
            label="Blur Amount"
            value={settings.blurKernelSize}
            min={1}
            max={15}
            step={2}
            onChange={(v) => onChange({ ...settings, blurKernelSize: v })}
          />
        </>
      )}

      {/* Common settings */}
      <Slider
        label="Line Thickness"
        value={settings.lineThickness}
        min={1}
        max={5}
        onChange={(v) => onChange({ ...settings, lineThickness: v })}
        unit="px"
      />

      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Invert Colors</label>
        <button
          onClick={() => onChange({ ...settings, invertColors: !settings.invertColors })}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
            ${settings.invertColors ? 'bg-indigo-500' : 'bg-gray-200'}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
              ${settings.invertColors ? 'translate-x-6' : 'translate-x-1'}
            `}
          />
        </button>
      </div>

      <button
        onClick={onPreview}
        disabled={isLoading}
        className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium
                   hover:bg-gray-200 transition-colors flex items-center justify-center gap-2
                   disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FiRefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        {isLoading ? 'Generating...' : 'Preview'}
      </button>
    </div>
  );
}

interface CleanupSettingsProps {
  settings: {
    blurRadius: number;
    contrast: number;
    brightness: number;
    threshold: number;
    sharpenAmount: number;
  };
  onChange: (settings: CleanupSettingsProps['settings']) => void;
  onPreview: () => void;
  isLoading?: boolean;
}

export function CleanupSettings({ settings, onChange, onPreview, isLoading }: CleanupSettingsProps) {
  return (
    <div className="space-y-6">
      <h3 className="font-semibold text-gray-900">Cleanup Settings</h3>

      <Slider
        label="Blur Radius"
        value={settings.blurRadius}
        min={0}
        max={3}
        step={0.1}
        onChange={(v) => onChange({ ...settings, blurRadius: v })}
      />

      <Slider
        label="Contrast"
        value={settings.contrast}
        min={0.5}
        max={3}
        step={0.1}
        onChange={(v) => onChange({ ...settings, contrast: v })}
      />

      <Slider
        label="Brightness"
        value={settings.brightness}
        min={0.5}
        max={2}
        step={0.1}
        onChange={(v) => onChange({ ...settings, brightness: v })}
      />

      <Slider
        label="Threshold"
        value={settings.threshold}
        min={0.1}
        max={0.9}
        step={0.05}
        onChange={(v) => onChange({ ...settings, threshold: v })}
      />

      <Slider
        label="Sharpen"
        value={settings.sharpenAmount}
        min={0}
        max={2}
        step={0.1}
        onChange={(v) => onChange({ ...settings, sharpenAmount: v })}
      />

      <button
        onClick={onPreview}
        disabled={isLoading}
        className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium
                   hover:bg-gray-200 transition-colors flex items-center justify-center gap-2
                   disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FiRefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        {isLoading ? 'Generating...' : 'Preview'}
      </button>
    </div>
  );
}
